<template>
  <CatalogCard
    :image="promotion.image"
    :name="promotion.name"
    :description="promotion.description"
    :reduction="promotion.discount_value"
    :reduction-type="promotion.discount_type"
    :has-unavailable-pass="hasUnavailablePass"
    is-stock-disabled
    v-bind="{ quantity, currency, price }"
    @increase-quantity="increaseQuantity"
    @decrease-quantity="decreaseQuantity">
    <template #actions="{ quantity }">
      <v-btn
        v-if="quantity >= 1"
        @click.stop="decreaseQuantity"
        color="red"
        width="50"
        depressed
        icon
        tile
        class="remove-promotion-btn"
        :disabled="isCartLoading">
        <v-icon>mdi-cart-off</v-icon>
      </v-btn>
    </template>
  </CatalogCard>
</template>

<script>
import {
  calculatePromotionPrice,
  extractVariantIdsFromGroup,
} from '@/utils/promotions'
import { getVariantCurrency } from '@/utils/variant'
import CatalogCard from './CatalogCard.vue'
import { mapGetters } from 'vuex'
import { convertNativeJsToFriendlyFireWeekdayIndex } from '@/utils/weekdays'

export default {
  name: 'CatalogPromotionCard',
  components: {
    CatalogCard,
  },
  props: {
    promotion: { type: Object, required: true },
    variants: { type: Array, default: () => [] },
    arenaCurrency: { type: Object, default: () => ({}) },
    quantity: { type: Number, default: 0 },
  },
  computed: {
    ...mapGetters('cart', ['isCartLoading']),
    price() {
      const price = calculatePromotionPrice(
        this.promotion,
        this.variants,
        this.arenaCurrency,
      )
      return price ? price.toFixed(2).toString() : '—'
    },
    currency() {
      return getVariantCurrency(this.variants[0])
    },
    hasUnavailablePass() {
      if (!this.promotion?.groups?.length) return false

      const jsWeekdayIndex = new Date().getDay()
      const dayIdx = convertNativeJsToFriendlyFireWeekdayIndex(jsWeekdayIndex)

      for (const group of this.promotion.groups) {
        const variantIds = extractVariantIdsFromGroup(group)
        if (!variantIds.length) continue

        for (const variantId of variantIds) {
          const variant = this.variants.find((v) => v.id === variantId)
          if (!variant) continue

          const weekdays = variant.attributes?.weekdays
          if (!weekdays?.length) continue
          if (!weekdays.includes(dayIdx)) return true
        }
      }
      return false
    },
  },
  methods: {
    decreaseQuantity() {
      if (this.isCartLoading) return
      const nextQuantity = Math.max(0, this.quantity - 1)
      this.$emit('update-quantity', {
        promotion: this.promotion,
        quantity: nextQuantity,
      })
    },
    increaseQuantity() {
      if (this.isCartLoading || this.quantity >= 1) return
      const nextQuantity = this.quantity + 1
      this.$emit('update-quantity', {
        promotion: this.promotion,
        quantity: nextQuantity,
      })
    },
  },
}
</script>

<style scoped lang="scss">
.catalog-card {
  min-height: 15rem;
}

::v-deep {
  .catalog-card__info {
    height: 3.25rem;
  }

  .catalog-card__content {
    gap: 0.75rem;
  }

  .catalog-card__name {
    -webkit-line-clamp: 1;
    line-height: 1rem;
  }

  .catalog-card__price {
    line-height: 1rem;
  }
}

.remove-promotion-btn {
  width: 100% !important;
}

@media (max-width: 599px) {
  ::v-deep .catalog-card {
    min-height: 18.75rem;
  }
}
</style>
